import { useEffect, useState } from 'react';
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  IconButton,
  Paper,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@material-ui/core';
import { Button } from '@backstage/ui';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import Alert from '@material-ui/lab/Alert';
import Autocomplete from '@material-ui/lab/Autocomplete';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { UserEntity } from '@backstage/catalog-model';
import {
  createScaffolderFieldExtension,
  FieldExtensionComponentProps,
} from '@backstage/plugin-scaffolder-react';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';

interface CatalogUserOption {
  email: string;
  displayName: string;
}

interface Principal {
  type: 'user' | 'group';
  email?: string;
  groupName?: string;
  permissions: string[];
}

// Labels shown to the user — must match PERMISSION_CODES_BY_LABEL in
// createPermissionTemplate.ts, which maps these back to SonarQube's API codes.
const PERMISSIONS: Array<{ label: string; description: string }> = [
  {
    label: 'Browse',
    description: 'View the project and its analysis results, issues, and measures.',
  },
  {
    label: 'See Source Code',
    description: "View the project's source code.",
  },
  {
    label: 'Administer Issues',
    description: 'Confirm, resolve, reopen, and reassign issues; change issue severity.',
  },
  {
    label: 'Administer Security Hotspots',
    description: 'Change the status of Security Hotspots.',
  },
  {
    label: 'Execute Analysis',
    description: 'Submit analysis results for this project (used by CI/CD tokens).',
  },
  {
    label: 'Administer',
    description: 'Full control: manage project settings, quality gates, and permissions.',
  },
];

const emptyPrincipal = (): Principal => ({ type: 'user', email: '', permissions: [] });

const PrincipalsEditorComponent = ({
  formData,
  onChange,
  rawErrors,
  disabled,
  schema,
}: FieldExtensionComponentProps<Principal[]>) => {
  const principals = formData ?? [];
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const catalogApi = useApi(catalogApiRef);
  const [groupOptions, setGroupOptions] = useState<string[]>([]);
  const [userOptions, setUserOptions] = useState<CatalogUserOption[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const baseUrl = await discoveryApi.getBaseUrl('sonarqube-directory');
        const response = await fetchApi.fetch(`${baseUrl}/groups`);
        if (!response.ok || cancelled) return;
        setGroupOptions(await response.json());
      } catch {
        // Live group list unavailable — group field falls back to free text.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [discoveryApi, fetchApi]);

  useEffect(() => {
    let cancelled = false;
    catalogApi
      .getEntities({
        filter: { kind: 'User' },
        fields: [
          'metadata.name',
          'metadata.title',
          'spec.profile.email',
          'spec.profile.displayName',
        ],
      })
      .then(({ items }) => {
        if (cancelled) return;
        const byEmail = new Map<string, CatalogUserOption>();
        for (const entity of items as UserEntity[]) {
          const email = entity.spec?.profile?.email;
          if (!email) continue;
          const key = email.toLowerCase();
          if (!byEmail.has(key)) {
            byEmail.set(key, {
              email,
              displayName:
                entity.spec?.profile?.displayName ??
                entity.metadata.title ??
                entity.metadata.name,
            });
          }
        }
        setUserOptions(
          [...byEmail.values()].sort((a, b) => a.email.localeCompare(b.email)),
        );
      });
    return () => {
      cancelled = true;
    };
  }, [catalogApi]);

  const updatePrincipal = (index: number, patch: Partial<Principal>) => {
    onChange(principals.map((p, i) => (i === index ? { ...p, ...patch } : p)));
  };

  const removePrincipal = (index: number) => {
    onChange(principals.filter((_, i) => i !== index));
  };

  const addPrincipal = () => {
    onChange([...principals, emptyPrincipal()]);
  };

  const togglePermission = (index: number, label: string) => {
    const current = principals[index].permissions ?? [];
    updatePrincipal(index, {
      permissions: current.includes(label)
        ? current.filter(l => l !== label)
        : [...current, label],
    });
  };

  return (
    <FormControl
      fullWidth
      required
      error={!!rawErrors?.length}
      disabled={disabled}
      component="fieldset"
    >
      <FormLabel component="legend">{schema.title ?? 'Principals'}</FormLabel>
      <Alert severity="warning" style={{ marginTop: 8, marginBottom: 8 }}>
        Only users who already have a SonarQube account will be added to the project. Anyone
        else is ignored — ask them to sign in to SonarQube once first, then rerun or add them
        manually.
      </Alert>

      <Box display="flex" flexWrap="wrap" alignItems="flex-start" style={{ gap: 16 }}>
        {principals.map((principal, index) => (
          <Paper
            key={index}
            variant="outlined"
            style={{
              position: 'relative',
              padding: 16,
              flex: '1 1 400px',
              maxWidth: 500,
              boxSizing: 'border-box',
            }}
          >
            <IconButton
              aria-label="Remove principal"
              size="small"
              disabled={disabled}
              onClick={() => removePrincipal(index)}
              style={{ position: 'absolute', top: 8, right: 8 }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>

            <RadioGroup
              row
              value={principal.type}
              onChange={e => {
                const nextType = e.target.value as 'user' | 'group';
                updatePrincipal(index, {
                  type: nextType,
                  email: nextType === 'user' ? '' : undefined,
                  groupName: nextType === 'group' ? '' : undefined,
                });
              }}
            >
              <FormControlLabel
                value="user"
                control={<Radio />}
                label="User"
                disabled={disabled}
              />
              <FormControlLabel
                value="group"
                control={<Radio />}
                label="SonarQube Group"
                disabled={disabled}
              />
            </RadioGroup>

            {principal.type === 'user' ? (
              <Autocomplete
                key="user"
                freeSolo
                disabled={disabled}
                options={userOptions}
                inputValue={principal.email ?? ''}
                getOptionLabel={(option: CatalogUserOption) => option.email}
                renderOption={(option: CatalogUserOption) =>
                  `${option.displayName} (${option.email})`
                }
                onChange={(_, value: CatalogUserOption | string | null) =>
                  updatePrincipal(index, {
                    email: typeof value === 'string' ? value : value?.email,
                  })
                }
                onInputChange={(_, value: string) => updatePrincipal(index, { email: value })}
                renderInput={params => (
                  <TextField {...params} label="Email" margin="dense" fullWidth />
                )}
              />
            ) : (
              <Autocomplete
                key="group"
                freeSolo
                disabled={disabled}
                options={groupOptions}
                value={principal.groupName ?? null}
                onChange={(_, value: string | null) =>
                  updatePrincipal(index, { groupName: value ?? undefined })
                }
                renderInput={params => (
                  <TextField {...params} label="SonarQube Group" margin="dense" fullWidth />
                )}
              />
            )}

            <FormLabel component="legend" style={{ marginTop: 16 }}>
              Permissions
            </FormLabel>
            <FormGroup>
              {PERMISSIONS.map(perm => (
                <Box key={perm.label} mb={0.5}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={principal.permissions?.includes(perm.label) ?? false}
                        onChange={() => togglePermission(index, perm.label)}
                        disabled={disabled}
                      />
                    }
                    label={perm.label}
                  />
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    style={{ display: 'block', marginLeft: 32, marginTop: -4 }}
                  >
                    {perm.description}
                  </Typography>
                </Box>
              ))}
            </FormGroup>
          </Paper>
        ))}

        <Box
          display="flex"
          alignItems="center"
          justifyContent="left"
          style={{ flex: '1 1 400px', maxWidth: 500, minHeight: 96 }}
        >
          <Button
            variant="primary"
            iconStart={<AddIcon />}
            isDisabled={disabled}
            onClick={addPrincipal}
          >
            Add principal
          </Button>
        </Box>
      </Box>

      {!!rawErrors?.length && <FormHelperText>{rawErrors[0]}</FormHelperText>}
    </FormControl>
  );
};

export const PrincipalsEditorFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension<Principal[]>({
    name: 'PrincipalsEditor',
    component: PrincipalsEditorComponent,
  }),
);
